import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { BsCheckCircleFill, BsClock, BsExclamationTriangle, BsHourglassSplit, BsQuestionCircle, BsTrash, BsXCircleFill } from 'react-icons/bs';
import { Menu } from '../../components/Menu/Menu';
import { automaticCloseAlert, confirmCloseAlert } from '../../functions/alerts';
import {
    DATA_SOURCE,
    GOAL_STATUS,
    GOAL_TYPE,
    computeProgress,
    deleteGoal,
    formatDate,
    formatDateRange,
    formatMoney,
    indexInvoices,
    loadGoalsData,
    salespersonLabel,
} from '../../services/goalsService';
import styles from '../../css/Goals.module.css';

const SOURCE_LABELS = {
    [DATA_SOURCE.real]: 'live data',
    [DATA_SOURCE.mock]: 'sample data',
    [DATA_SOURCE.unavailable]: 'unavailable',
};

const STATUS_DISPLAY = {
    [GOAL_STATUS.upcoming]: { label: 'Upcoming', Icon: BsClock, badge: styles.badgeUpcoming, bar: '' },
    [GOAL_STATUS.inProgress]: { label: 'In progress', Icon: BsHourglassSplit, badge: styles.badgeInProgress, bar: styles.meterFillInProgress },
    [GOAL_STATUS.achieved]: { label: 'Achieved', Icon: BsCheckCircleFill, badge: styles.badgeAchieved, bar: styles.meterFillAchieved },
    [GOAL_STATUS.missed]: { label: 'Missed', Icon: BsXCircleFill, badge: styles.badgeMissed, bar: styles.meterFillMissed },
    [GOAL_STATUS.noData]: { label: 'No sales data', Icon: BsQuestionCircle, badge: styles.badgeUpcoming, bar: '' },
};

export const goalStatusLabel = (status) => STATUS_DISPLAY[status].label;

export const goalDetailPath = (goalType, targetNo) => `/goals/${goalType}/${encodeURIComponent(targetNo)}`;

// Nombre visible del vendedor o customer de una meta; un código sin catálogo se muestra tal cual.
export const useTargetNames = (data) => useMemo(() => {
    const customers = new Map();
    const salespeople = new Map();
    if (data) {
        data.customers.forEach((c) => customers.set(c.customerNo, c.name));
        data.salespeople.forEach((s) => salespeople.set(s.salespersonNo, salespersonLabel(s)));
    }
    return (goalType, targetNo) => (
        (goalType === GOAL_TYPE.customer ? customers : salespeople).get(targetNo) || targetNo
    );
}, [data]);

export const useGoalsWithProgress = (data) => useMemo(() => {
    if (!data) return [];
    const salesIndex = data.invoices ? indexInvoices(data.invoices) : null;
    return data.goals.map((g) => computeProgress(g, salesIndex, data.today));
}, [data]);

export function useGoalsData(idCompany, loadingRef) {
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState('');

    const load = useCallback(async () => {
        loadingRef.current.showState(true);
        setLoadError('');
        let loaded = null;
        try {
            loaded = await loadGoalsData(idCompany);
        } catch (e) {
            console.error('Could not load the goals:', e);
        }
        // Sin ref = el usuario ya salió de la pantalla mientras cargaba.
        if (!loadingRef.current) return;
        if (loaded) setData(loaded);
        else setLoadError('Could not load the goals. Try again please.');
        loadingRef.current.showState(false);
    }, [idCompany, loadingRef]);

    useEffect(() => { load(); }, [load]);

    return { data, loadError, reload: load };
}

export function useGoalDelete(idCompany, data, targetName, reload) {
    return useCallback(async (goal) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Delete this goal?',
            text: `${targetName(goal.goalType, goal.targetNo)}: ${formatMoney(goal.targetAmount)}, ${formatDateRange(goal.startDate, goal.endDate)}.`,
            showCancelButton: true,
            confirmButtonText: 'Delete goal',
            confirmButtonColor: '#B91C1C',
            cancelButtonText: 'Keep it',
        });
        if (!result.isConfirmed) return;
        try {
            await deleteGoal(idCompany, data.scope, goal.id);
            await reload();
            automaticCloseAlert('correct', 'Goal deleted');
        } catch (e) {
            confirmCloseAlert('incorrect', e.message);
        }
    }, [idCompany, data, targetName, reload]);
}

export function GoalsDenied() {
    return (
        <div className={styles.page}>
            <Menu />
            <div className={styles.denied}>This view is only available to administrators.</div>
        </div>
    );
}

export function GoalsDemoNotice({ data }) {
    const usesMock = [data.customersSource, data.salespeopleSource, data.salesSource].includes(DATA_SOURCE.mock);
    const salesUnavailable = data.salesSource === DATA_SOURCE.unavailable;
    return (
        <div className={`${styles.notice} ${usesMock || salesUnavailable ? styles.noticeMock : ''}`} role="status">
            <BsExclamationTriangle className={styles.noticeIcon} aria-hidden="true" />
            <div>
                <strong>Demo:</strong> goals are saved in this browser only, and sample goals were added to start.
                {' '}Customers: <strong>{SOURCE_LABELS[data.customersSource]}</strong> ({data.customers.length.toLocaleString()}).
                {' '}Salespeople: <strong>{SOURCE_LABELS[data.salespeopleSource]}</strong> ({data.salespeople.length}).
                {' '}Sales: <strong>{SOURCE_LABELS[data.salesSource]}</strong>
                {data.invoicesThrough ? `, through ${formatDate(data.invoicesThrough)}` : ''}.
                {salesUnavailable && ' Progress cannot be shown until sales load. Reload the page to try again.'}
            </div>
        </div>
    );
}

export function GoalStatusBadge({ status }) {
    const { label, Icon, badge } = STATUS_DISPLAY[status];
    return (
        <span className={`${styles.badge} ${badge}`}>
            <Icon aria-hidden="true" /> {label}
        </span>
    );
}

export function GoalMeter({ goal, large }) {
    if (goal.status === GOAL_STATUS.upcoming) {
        return <p className={styles.meterNote}>Starts {formatDate(goal.startDate)}</p>;
    }
    if (goal.percent === null) {
        return <p className={styles.meterNote}>Sales not available</p>;
    }
    const percent = Math.round(goal.percent);
    const width = Math.min(Math.max(goal.percent, 0), 100);
    return (
        <div className={styles.meter}>
            <div
                className={`${styles.meterTrack} ${large ? styles.meterTrackLarge : ''}`}
                role="progressbar"
                aria-valuenow={Math.round(width)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuetext={`${percent}% of ${formatMoney(goal.targetAmount)}`}
            >
                <div className={`${styles.meterFill} ${STATUS_DISPLAY[goal.status].bar}`} style={{ width: `${width}%` }} />
            </div>
            <span className={styles.meterPercent}>{percent}%</span>
        </div>
    );
}

const soldText = (goal) => {
    if (goal.status === GOAL_STATUS.upcoming) return '—';
    return goal.achieved === null ? 'Not available' : formatMoney(goal.achieved);
};

// En teléfono cada fila se vuelve una tarjeta; data-label pone el título que da el encabezado.
export function GoalsTable({ goals, targetName, showTarget, showCreated, onDelete, caption }) {
    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <caption className={styles.srOnly}>{caption}</caption>
                <thead>
                    <tr>
                        {showTarget && <th scope="col">Goal for</th>}
                        <th scope="col">Dates</th>
                        <th scope="col" className={styles.numeric}>Expected</th>
                        <th scope="col" className={styles.numeric}>Sold</th>
                        <th scope="col">Progress</th>
                        <th scope="col">Status</th>
                        {showCreated && <th scope="col">Created</th>}
                        {onDelete && <th scope="col"><span className={styles.srOnly}>Actions</span></th>}
                    </tr>
                </thead>
                <tbody>
                    {goals.map((g) => (
                        <tr key={g.id}>
                            {showTarget && (
                                <td data-label="Goal for" className={styles.cellPrimary}>
                                    <Link className={styles.tableLink} to={goalDetailPath(g.goalType, g.targetNo)}>{targetName(g.goalType, g.targetNo)}</Link>
                                    <span className={styles.cellNote}>{g.goalType === GOAL_TYPE.customer ? 'Customer' : 'Salesperson'}</span>
                                </td>
                            )}
                            <td data-label="Dates">{formatDateRange(g.startDate, g.endDate)}</td>
                            <td data-label="Expected" className={styles.numeric}>{formatMoney(g.targetAmount)}</td>
                            <td data-label="Sold" className={styles.numeric}>{soldText(g)}</td>
                            <td data-label="Progress" className={styles.cellMeter}><GoalMeter goal={g} /></td>
                            <td data-label="Status"><GoalStatusBadge status={g.status} /></td>
                            {showCreated && (
                                <td data-label="Created">
                                    <span className={styles.cellStack}>
                                        {formatDate(g.createdAt.slice(0, 10))}
                                        <span className={styles.cellNote}>{g.createdBy}</span>
                                    </span>
                                </td>
                            )}
                            {onDelete && (
                                <td className={styles.cellActions}>
                                    <button
                                        type="button"
                                        className={styles.btnDanger}
                                        onClick={() => onDelete(g)}
                                        aria-label={`Delete goal for ${targetName(g.goalType, g.targetNo)}, ${formatDateRange(g.startDate, g.endDate)}`}
                                    >
                                        <BsTrash aria-hidden="true" /> Delete
                                    </button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
